import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Banner from './Banner';
import { useProteoRisk } from './context';
import { StatusBadge, formatDate, jobSummary } from './jobs';
import { SERVICE_CODE, submitAnalysis } from './api';
import { checkWorkbook } from './workbook';
import { EXAMPLE_WORKBOOK, ExampleWorkbookLink } from './exampleFiles';
import { SystemIcon } from './report/Visuals';
import WebserverDemoSection from '../../shared/WebserverDemoSection';

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${parseFloat((bytes / 1024 ** index).toFixed(1))} ${units[index]}`;
}

export function ageError(value) {
  const text = String(value).trim();
  if (!text) return 'Enter the age in whole years.';
  if (!/^\d+$/.test(text)) return 'Enter the age as a whole number, for example 58.';
  const age = Number(text);
  if (age < 1 || age > 129) return 'Age must be between 1 and 129.';
  return '';
}

export function sexError(value) {
  return value === 'Male' || value === 'Female' ? '' : 'Choose Male or Female.';
}

export function bmiValue(value) {
  const text = String(value).trim().replace(',', '.');
  return text === '' ? null : Number(text);
}

export function bmiError(value) {
  const bmi = bmiValue(value);
  if (bmi === null) return '';
  if (!Number.isFinite(bmi)) return 'Enter BMI as a number, for example 24.5, or leave it empty.';
  if (bmi < 5 || bmi > 100) return 'BMI must be between 5 and 100, or leave it empty.';
  return '';
}

const STATUS_FILTERS = [
  ['all', 'All'],
  ['running', 'Scoring'],
  ['queued', 'Waiting in queue'],
  ['completed', 'Ready'],
  ['failed', 'Failed'],
  ['pending_upload', 'Upload not finished'],
];

function JobsList() {
  const { jobs, jobsState } = useProteoRisk();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const term = search.trim().toLowerCase();
  const shown = jobs.filter((job) => (filter === 'all' || job.status === filter)
    && (!term || String(job.sample_name).toLowerCase().includes(term)
      || String(job.upload_uuid).toLowerCase().includes(term)));

  let body;
  if (jobsState.loading) {
    body = <div className="PROTEORISK-empty-state"><i className="fas fa-spinner fa-spin" /><span>Loading...</span></div>;
  } else if (jobsState.error) {
    body = <div className="PROTEORISK-empty-state error"><i className="fas fa-exclamation-circle" /><span>{jobsState.error}</span></div>;
  } else if (!shown.length) {
    body = (
      <div className="PROTEORISK-empty-state">
        <i className="fas fa-folder-open" />
        <span>{jobs.length ? 'No analyses match this search' : 'No analyses yet'}</span>
        {!jobs.length && (
          <span className="PROTEORISK-empty-links">
            <Link to="/proteorisk/example" className="PROTEORISK-link">See an example report</Link>
            <Link to="/proteorisk/info" className="PROTEORISK-link">How ProteoRisk works</Link>
          </span>
        )}
      </div>
    );
  } else {
    body = shown.map((job) => (
      <Link key={job.upload_uuid} to={`/proteorisk/jobs/${job.upload_uuid}`} className="PROTEORISK-job-item">
        <div className="PROTEORISK-job-icon"><SystemIcon code="blood" size={20} /></div>
        <div className="PROTEORISK-job-content">
          <div className="PROTEORISK-job-title">{job.sample_name}</div>
          <div className="PROTEORISK-job-subtitle">
            {[jobSummary(job.metadata), formatDate(job.created_at)].filter(Boolean).join(' · ')}
          </div>
        </div>
        <StatusBadge status={job.status} />
      </Link>
    ));
  }

  return (
    <div className="PROTEORISK-section PROTEORISK-jobs-section">
      <h2><i className="fas fa-tasks" aria-hidden="true" /> Your analyses</h2>
      <div className="PROTEORISK-jobs-filter">
        <div className="PROTEORISK-search-input">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search analyses..."
            aria-label="Search analyses"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <select value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Filter by status">
          {STATUS_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div className="PROTEORISK-jobs-list">{body}</div>
    </div>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return <span className="PROTEORISK-field-error"><i className="fas fa-exclamation-circle" aria-hidden="true" /> {message}</span>;
}

function NewAnalysis() {
  const { cookieId, contract, contractError, refreshJobs } = useProteoRisk();
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [fileCheck, setFileCheck] = useState({ state: 'idle' });
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [bmi, setBmi] = useState('');
  const [sampleCode, setSampleCode] = useState('');
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitError, setSubmitError] = useState('');

  const sampleCodes = contract ? contract.sampleCodes : [];
  const chosenSampleCode = sampleCodes.length === 1 ? sampleCodes[0] : sampleCode;

  const errors = {
    file: !file ? 'Choose the Olink Explore HT workbook.' : (fileCheck.state === 'error' ? fileCheck.error : ''),
    age: ageError(age),
    sex: sexError(sex),
    bmi: bmiError(bmi),
    sampleCode: sampleCodes.length > 1 && !sampleCode ? 'Choose the sample type.' : '',
  };
  const show = (field) => (attempted || touched[field]) && errors[field];
  const touch = (field) => setTouched((current) => ({ ...current, [field]: true }));

  const chooseFile = async (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    touch('file');
    setSubmitError('');
    setFileCheck({ state: 'checking' });
    try {
      const result = await checkWorkbook(chosen);
      setFileCheck(result.ok ? { state: 'ok', ...result } : { state: 'error', error: result.error });
    } catch (error) {
      setFileCheck({ state: 'error', error: `The workbook could not be checked: ${error.message}` });
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileCheck({ state: 'idle' });
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setAttempted(true);
    setSubmitError('');
    if (!contract || fileCheck.state === 'checking' || Object.values(errors).some(Boolean)) return;
    setSubmitting(true);
    setProgress(0);
    try {
      const { created } = await submitAnalysis({
        token: cookieId,
        contract,
        sampleCode: chosenSampleCode,
        file,
        displayName: name.trim() || file.name.replace(/\.xlsx$/i, ''),
        age: Number(age),
        sex,
        bmi: bmiValue(bmi),
        onProgress: ({ fraction }) => setProgress(Math.round(fraction * 100)),
      });
      await refreshJobs(false);
      navigate(`/proteorisk/jobs/${created.uuid}`);
    } catch (error) {
      setSubmitError(error.message || 'The analysis could not be started.');
      setSubmitting(false);
    }
  };

  const unavailable = contractError;

  return (
    <form className="PROTEORISK-section PROTEORISK-newjob-section" onSubmit={handleSubmit} noValidate>
      <h2><i className="fas fa-plus-circle" aria-hidden="true" /> New ProteoRisk analysis</h2>

      {unavailable && (
        <div className="PROTEORISK-message error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden="true" />
          <span>{unavailable}</span>
        </div>
      )}

      <div className="PROTEORISK-form-group">
        <label htmlFor="proteorisk-workbook">
          Olink Explore HT workbook <span className="PROTEORISK-required">*</span>
        </label>
        <div
          id="proteorisk-workbook"
          role="button"
          tabIndex={0}
          className={`PROTEORISK-dropzone ${file ? 'has-file' : ''} ${dragging ? 'dragging' : ''} ${show('file') ? 'invalid' : ''}`}
          onClick={() => { if (!submitting) fileInput.current?.click(); }}
          onKeyDown={(event) => {
            if ((event.key === 'Enter' || event.key === ' ') && !submitting) {
              event.preventDefault();
              fileInput.current?.click();
            }
          }}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (!submitting) chooseFile(event.dataTransfer.files?.[0]);
          }}
        >
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={(event) => chooseFile(event.target.files?.[0])}
            disabled={submitting}
          />
          {file ? (
            <div className="PROTEORISK-dropzone-file">
              <i className="fas fa-file-excel" aria-hidden="true" />
              <div className="PROTEORISK-dropzone-file-info">
                <span className="PROTEORISK-dropzone-filename">{file.name}</span>
                <span className="PROTEORISK-dropzone-filesize">{formatFileSize(file.size)}</span>
              </div>
              <button
                type="button"
                className="PROTEORISK-dropzone-remove"
                aria-label="Remove workbook"
                disabled={submitting}
                onClick={(event) => { event.stopPropagation(); clearFile(); }}
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="PROTEORISK-dropzone-empty">
              <i className="fas fa-cloud-upload-alt" aria-hidden="true" />
              <span>Drop the .xlsx workbook here or click to browse</span>
              <small>One sample, on a sheet named Data</small>
            </div>
          )}
        </div>
        {fileCheck.state === 'checking' && (
          <span className="PROTEORISK-field-note"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Checking the workbook...</span>
        )}
        {fileCheck.state === 'ok' && (
          <span className="PROTEORISK-field-ok">
            <i className="fas fa-check-circle" aria-hidden="true" />
            {` Looks good: ${fileCheck.proteinColumns.toLocaleString('en-US')} protein columns, ${fileCheck.measured.toLocaleString('en-US')} with a value.`}
          </span>
        )}
        <FieldError message={show('file') ? errors.file : ''} />
        <span className="PROTEORISK-field-note">
          Not sure about the layout?
          {' '}
          <ExampleWorkbookLink>Download the example workbook</ExampleWorkbookLink>
          {' '}
          <span
            className="PROTEORISK-field-muted"
            title={`Made-up values. With age ${EXAMPLE_WORKBOOK.age}, sex ${EXAMPLE_WORKBOOK.sex} and BMI ${EXAMPLE_WORKBOOK.bmi} it gives the example report.`}
          >
            (made-up values)
          </span>
        </span>
      </div>

      <div className="PROTEORISK-form-row">
        <div className="PROTEORISK-form-group">
          <label htmlFor="proteorisk-age">Age <span className="PROTEORISK-required">*</span></label>
          <input
            id="proteorisk-age"
            type="number"
            inputMode="numeric"
            min="1"
            max="129"
            step="1"
            placeholder="Whole years, 1 to 129"
            value={age}
            className={show('age') ? 'invalid' : ''}
            onChange={(event) => setAge(event.target.value)}
            onBlur={() => touch('age')}
            disabled={submitting}
          />
          <FieldError message={show('age') ? errors.age : ''} />
        </div>
        <div className="PROTEORISK-form-group">
          <label htmlFor="proteorisk-sex">Sex <span className="PROTEORISK-required">*</span></label>
          <select
            id="proteorisk-sex"
            value={sex}
            className={show('sex') ? 'invalid' : ''}
            onChange={(event) => { setSex(event.target.value); touch('sex'); }}
            onBlur={() => touch('sex')}
            disabled={submitting}
          >
            <option value="">Select...</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          <FieldError message={show('sex') ? errors.sex : ''} />
        </div>
      </div>

      <div className="PROTEORISK-form-row">
        <div className="PROTEORISK-form-group">
          <label htmlFor="proteorisk-bmi">BMI <span className="PROTEORISK-optional">optional</span></label>
          <input
            id="proteorisk-bmi"
            type="number"
            inputMode="decimal"
            min="5"
            max="100"
            step="0.1"
            placeholder="5 to 100"
            value={bmi}
            className={show('bmi') ? 'invalid' : ''}
            onChange={(event) => setBmi(event.target.value)}
            onBlur={() => touch('bmi')}
            disabled={submitting}
          />
          <FieldError message={show('bmi') ? errors.bmi : ''} />
        </div>
        <div className="PROTEORISK-form-group">
          <label htmlFor="proteorisk-name">Name for this analysis <span className="PROTEORISK-optional">optional</span></label>
          <input
            id="proteorisk-name"
            type="text"
            maxLength={120}
            placeholder="e.g. Sample A"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      {sampleCodes.length > 1 && (
        <div className="PROTEORISK-form-group">
          <label htmlFor="proteorisk-sample">Sample type <span className="PROTEORISK-required">*</span></label>
          <select
            id="proteorisk-sample"
            value={sampleCode}
            className={show('sampleCode') ? 'invalid' : ''}
            onChange={(event) => { setSampleCode(event.target.value); touch('sampleCode'); }}
            disabled={submitting}
          >
            <option value="">Select...</option>
            {sampleCodes.map((code) => <option key={code} value={code}>{code}</option>)}
          </select>
          <FieldError message={show('sampleCode') ? errors.sampleCode : ''} />
        </div>
      )}

      <p className="PROTEORISK-form-hint">
        <i className="fas fa-info-circle" aria-hidden="true" />
        Protein levels change with age, sex and BMI, so ProteoRisk adjusts every protein for them. Only the
        protein values in the workbook are used; the DAid, barcode, Cohort and Diagnose columns can be left empty.
      </p>

      {submitError && (
        <div className="PROTEORISK-message error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden="true" />
          <span>{submitError}</span>
        </div>
      )}

      {submitting && (
        <div className="PROTEORISK-upload-progress">
          <div className="PROTEORISK-progress-bar">
            <div className="PROTEORISK-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="PROTEORISK-progress-text">{progress}%</span>
        </div>
      )}

      <button
        type="submit"
        className="PROTEORISK-submit-btn"
        disabled={submitting || !contract || fileCheck.state === 'checking'}
      >
        {submitting
          ? <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Uploading...</>
          : <><i className="fas fa-arrow-right" aria-hidden="true" /> Start analysis</>}
      </button>
    </form>
  );
}

export default function Analyze() {
  return (
    <div className="PROTEORISK-home">
      <Banner title="Welcome to ProteoRisk" subtitle="Disease risk scoring from blood proteins" />
      <div className="PROTEORISK-home-content">
        <section className="PROTEORISK-left-column"><JobsList /></section>
        <aside className="PROTEORISK-right-column">
          <NewAnalysis />
          <WebserverDemoSection serviceCode={SERVICE_CODE} />
        </aside>
      </div>
    </div>
  );
}
